import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
}

interface SegmentedControlProps<T extends string> {
  options: SegmentedOption<T>[];
  value: T | null;
  onChange: (value: T) => void;
  /** Render option labels in the mono face (for numeric/technical values). */
  mono?: boolean;
  className?: string;
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  mono,
  className,
}: SegmentedControlProps<T>) {
  return (
    <div
      role="radiogroup"
      className={cn(
        "flex w-full max-w-full flex-wrap gap-1 rounded-xl border border-studio-border bg-studio-card p-1 sm:inline-flex sm:w-auto",
        className
      )}
    >
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt.value)}
            className={cn(
              "min-h-11 min-w-0 flex-1 basis-[calc(50%_-_0.25rem)] whitespace-normal rounded-lg px-2.5 py-1.5 text-center text-xs font-medium leading-4 transition-[background-color,color,box-shadow] sm:min-h-8 sm:flex-initial sm:basis-auto sm:px-3",
              mono && "font-mono",
              active
                ? "bg-studio-accent text-studio-accent-fg shadow-sm shadow-studio-accent/30"
                : "text-studio-muted hover:text-studio-ink"
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
